// ============================================
// SearchInput Component
// Search input with icon for tables and filters
// ============================================

import { InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../utils/cn';
import { SearchIcon } from '../icons';

// ============================================
// Types
// ============================================

interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
  /** Search value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Width (CSS value) */
  width?: string;
  /** Size variant */
  inputSize?: 'sm' | 'md';
  /** Additional className */
  className?: string;
}

// ============================================
// Component
// ============================================

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      placeholder = 'Search...',
      width,
      inputSize = 'md',
      className,
      ...props
    },
    ref
  ) => {
    const sizeStyles = {
      sm: 'h-9 text-xs',
      md: 'h-10 text-sm',
    };

    return (
      <div
        className={cn('relative', className)}
        style={{ width: width || '300px' }}
      >
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
          <SearchIcon size={inputSize === 'sm' ? 14 : 16} />
        </div>
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          enterKeyHint="search"
          className={cn(
            'w-full rounded-lg border border-slate-200 bg-white pl-10 pr-3 text-slate-700 placeholder-slate-400 shadow-sm transition-colors',
            'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
            sizeStyles[inputSize]
          )}
          {...props}
          onKeyDown={(e) => {
            // Enter -> blur để mobile đóng bàn phím ảo (bug #116)
            props.onKeyDown?.(e);
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
        />
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
